import { Bell, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import type { EmailTemplate } from '@/lib/admin/email-templates/models/email-template.model';

interface RecipientsSummaryProps {
  template: Pick<EmailTemplate, 'recipients' | 'notification_recipients'>;
}

type RecipientScope = 'both' | 'email' | 'notification';

const SCOPE_CLASSES: Record<RecipientScope, string> = {
  both: 'bg-green-50 text-green-700',
  email: 'bg-blue-50 text-blue-700',
  notification: 'bg-amber-50 text-amber-700',
};

const SCOPE_LABELS: Record<RecipientScope, string> = {
  both: 'emailTemplates.table.scopeBoth',
  email: 'emailTemplates.table.scopeEmailOnly',
  notification: 'emailTemplates.table.scopeNotificationOnly',
};

/**
 * Colonna "Destinatari" della tabella unificata: un chip per destinatario, non
 * una riga per canale.
 *
 * Lo stesso token compare quasi sempre su entrambi i canali, e ripeterlo due
 * volte raddoppiava l'altezza della riga senza aggiungere informazione. Qui il
 * canale lo dicono colore e icone: verde con ✉ e 🔔 = email + notifica, blu con ✉
 * = solo email, giallo con 🔔 = solo notifica. Le icone non sono decorazione: il
 * colore da solo non basta a chi non lo distingue, e "attivo su quale canale" è
 * l'informazione che a DB non esiste come colonna e si legge solo dai destinatari.
 */
export const RecipientsSummary = ({ template }: RecipientsSummaryProps) => {
  const { t } = useI18n();

  const emailSet = new Set(template.recipients);
  const notificationSet = new Set(template.notification_recipients);

  // Email prima, poi i soli-notifica: l'ordine resta stabile fra le righe.
  const recipients = [
    ...template.recipients,
    ...template.notification_recipients.filter((token) => !emailSet.has(token)),
  ];

  if (recipients.length === 0) {
    return (
      <span className="text-xs italic text-gray-400">{t('emailTemplates.table.noRecipients')}</span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {recipients.map((recipient) => {
        const scope: RecipientScope =
          emailSet.has(recipient) && notificationSet.has(recipient)
            ? 'both'
            : emailSet.has(recipient)
              ? 'email'
              : 'notification';

        return (
          <Badge
            key={recipient}
            variant="secondary"
            title={t(SCOPE_LABELS[scope] as 'emailTemplates.table.scopeBoth')}
            className={cn(
              'gap-1 border-transparent px-1.5 py-0.5 text-[11px] font-medium',
              SCOPE_CLASSES[scope]
            )}
          >
            {scope !== 'notification' && <Mail className="h-3 w-3 shrink-0" aria-hidden />}
            {scope !== 'email' && <Bell className="h-3 w-3 shrink-0" aria-hidden />}
            {recipient}
          </Badge>
        );
      })}
    </div>
  );
};
